import {
	FormOptional,
	FormShape,
	FormSwitch,
	FormUnit,
	type GetFormInput,
	type GetFormInputSetter,
	type GetFormOutputVerbose,
} from "@suse-o11y/signal/form";
import { z } from "zod";

import { ALL_CALCULATIONS } from "~/tools/calculate-value-from-points";
import { ImmutableSet } from "~/tools/immutable-set";
import { isSetDirty } from "~/tools/is-set-dirty";
import { readonly } from "~/tools/readonly";

import { ALL_MODES, ALL_POSITIONS, type Columns, DEFAULT, type Mode, type Position, type Settings } from "./settings";

const positionSchema = z.enum(ALL_POSITIONS);
type PositionFormState = FormUnit<string, ReadonlyArray<string>, Position>;

const modeSchema = z.enum(ALL_MODES);
type ModeFormState = FormUnit<string, ReadonlyArray<string>, Mode>;

type TableFormState = FormUnit<ReadonlyArray<string>, ReadonlyArray<string>, Columns>;
const tableSchema = z.array(z.enum(ALL_CALCULATIONS)).transform(ImmutableSet.fromArray);

interface Sync {
	enabled?: boolean;
	position?: GetFormInput<PositionFormState>;
	mode?: GetFormInput<ModeFormState>;
	table?: GetFormInput<TableFormState>;
}

type FormState = FormOptional<
	FormUnit<boolean>,
	FormShape<{
		position: PositionFormState;
		mode: FormSwitch<
			ModeFormState,
			{
				list: FormUnit<true>;
				table: TableFormState;
			}
		>;
	}>
>;

const INITIAL: GetFormInput<FormState> = {
	enabled: true,
	element: {
		position: DEFAULT.position,
		mode: {
			active: DEFAULT.mode,
			branches: {
				list: true,
				table: DEFAULT.columns.toArray(),
			},
		},
	},
};

function fromSettings(settings: undefined | Settings) {
	return {
		enabled: settings?.show,
		element: {
			position: settings?.position?.toString(),
			mode: {
				active: settings?.mode?.toString(),
				branches: {
					list: INITIAL.element.mode.branches.list,
					table: readonly(settings?.columns?.toArray().map((column) => column.toString())),
				},
			},
		},
	} satisfies GetFormInputSetter<FormState>;
}

type Input = ReturnType<typeof fromSettings>;

function init(initial: Input): FormState {
	return FormOptional(
		FormUnit(initial.enabled ?? INITIAL.enabled),
		FormShape({
			position: FormUnit(initial.element.position ?? INITIAL.element.position, {
				schema: positionSchema,
			}),
			mode: FormSwitch(
				FormUnit(initial.element.mode.active ?? INITIAL.element.mode.active, {
					schema: modeSchema,
				}),
				{
					list: FormUnit(initial.element.mode.branches.list),
					table: FormUnit(initial.element.mode.branches.table ?? INITIAL.element.mode.branches.table, {
						schema: tableSchema,
						isInputDirty: isSetDirty,
					}),
				},
			),
		}),
	);
}

function toSettings(output: GetFormOutputVerbose<FormState>): Settings {
	return {
		show: output.enabled ?? undefined,
		position: output.element.position ?? undefined,
		mode: output.element.mode.active ?? undefined,
		columns: output.element.mode.branches.table ?? undefined,
	};
}

function fromSync({ enabled, position, mode, table }: Sync): Input {
	return {
		enabled,
		element: {
			position,
			mode: {
				active: mode,
				branches: { table, list: true },
			},
		},
	};
}

function toSync({ enabled, element: { position, mode } }: Input): Sync {
	return {
		enabled,
		position,
		mode: mode.active,
		table: mode.branches.table,
	};
}

const FormState = {
	initial: INITIAL,
	init,
	fromSettings,
	toSettings,
	fromSync,
	toSync,
} as const;

export { FormState };
export type { PositionFormState, ModeFormState, TableFormState, Sync };
